/**
 * Client des exports comptables mensuels (KPMG).
 *
 * Même traitement d'erreur que le reste de l'application : il passe par
 * `request`, donc une session expirée redirige au lieu de laisser l'écran
 * dans un état cassé.
 */
import { ApiError, request } from './api';

export type StatutExport = 'en_cours' | 'genere' | 'transmis' | 'annule' | 'echec';

export interface TotauxRapport {
  fichier: string;
  pieces: number;
  lignes: number;
  totalTtc: number;
  rattrapees: Array<{ numero: string; date: string; montant: number; tiers: string }>;
}

export interface PieceModifiee {
  zohoId: string;
  numero: string;
  type: string;
  montantRegistre: number;
  montantActuel: number;
  formeActuelle: string;
}

export interface ResumeExport {
  id: string;
  mois: string;
  statut: StatutExport;
  genere_par: string;
  genere_at: string;
  transmis_at: string | null;
  annule_at: string | null;
  annule_motif: string | null;
  progression?: string | null;
  erreur?: string | null;
  pieces: number;
  fichiers: Array<{ nom: string; chemin: string; octets: number }>;
  exclusions: Array<{ type: string; numero: string; tiers: string; motif: string }>;
  totaux: {
    rapports?: Record<string, TotauxRapport>;
    modifiees?: PieceModifiee[];
    refusees?: Array<{ type: string; numero: string; motif: string }>;
    /**
     * Les pièces parties avec leur colonne « Annule et remplace » vide : on n'a
     * pas su retrouver le numéro de la facture qu'elles annulent.
     *
     * Les types de ce fichier sont écrits À LA MAIN, rien n'est généré depuis le
     * serveur : un champ oublié ici ne fait échouer aucune compilation, il
     * arrive simplement `undefined` à l'écran. D'où ce commentaire plutôt qu'une
     * confiance dans le typecheck.
     */
    referencesIntrouvables?: Array<{ numero: string; identifiantCherche: string }>;
    /** Total des pièces retenues : zéro = clôture vide. */
    piecesTotal?: number;
    dejaTransmises?: number;
    /**
     * Ce qui suit n'existe que sur l'export d'INITIALISATION du registre.
     * Il était écrit en base depuis le début et n'était affiché nulle part :
     * Valérie n'avait donc aucun moyen de savoir quels classeurs avaient servi
     * (13/10/2026).
     */
    amorcage?: boolean;
    fichiers?: Array<{ nom: string; mois: string; cle: string; pieces: number }>;
    avantLesFichiers?: number;
    ignorees?: string[];
    renumerotations?: Array<{ cle: string; idBooks: string }>;
  };
}

export const comptaApi = {
  liste: () =>
    request<{
      exports: ResumeExport[];
      registreAmorce: boolean;
      depot: { racine: string; dossierDeTest: boolean; incoherent: boolean };
    }>('/api/compta/exports'),
  detail: (id: string) => request<ResumeExport>(`/api/compta/exports/${id}`),
  generer: (mois: string) => request<{ id: string }>('/api/compta/exports', { method: 'POST', body: JSON.stringify({ mois }) }),
  deposer: (id: string) => request<{ fichiers: Array<{ nom: string; chemin: string }> }>(`/api/compta/exports/${id}/dropbox`, { method: 'POST' }),
  transmettre: (id: string) => request<{ ok: true }>(`/api/compta/exports/${id}/transmis`, { method: 'POST' }),
  annuler: (id: string, motif: string) => request<{ ok: true; piecesLiberees: number }>(`/api/compta/exports/${id}/annuler`, { method: 'POST', body: JSON.stringify({ motif }) }),
  lienFichier: (id: string, nom: string) => `/api/compta/exports/${id}/fichiers/${encodeURIComponent(nom)}`,
};

// ─── Initialisation du registre ────────────────────────────────────────────

export interface AnalyseAmorcage {
  mois: string[];
  fichiers: Array<{ nom: string; cle: string; mois: string; lignes: number; entrees: Array<{ cle: string }> }>;
  manquants: Array<{ mois: string; cle: string; libelle: string; facultatif: boolean }>;
  avantLesFichiers: number;
  appariees: number;
  introuvables: Array<{
    cle: string;
    numero: string;
    date: string;
    montant: number;
    fichier: string;
    probableRenumerotation?: { id: string; numero: string; date: string; montant: number; tiers: string };
  }>;
}

/** Les classeurs sont déposés, jamais conservés : un envoi multipart, pas du JSON. */
function formulaire(input: { fichiers: File[]; mois: string[]; sansAvoir: Array<{ mois: string; cle: string }>; ignorees?: string[]; renumerotations?: Array<{ cle: string; idBooks: string }>; instantaneId?: string }): FormData {
  const f = new FormData();
  for (const fichier of input.fichiers) f.append('fichiers', fichier);
  f.append('mois', JSON.stringify(input.mois));
  f.append('sansAvoir', JSON.stringify(input.sansAvoir));
  if (input.ignorees) f.append('ignorees', JSON.stringify(input.ignorees));
  if (input.renumerotations) f.append('renumerotations', JSON.stringify(input.renumerotations));
  // L'instantané de l'analyse : le lancement évite ainsi de relire tout Books
  // une seconde fois (six minutes). C'est un identifiant opaque.
  if (input.instantaneId) f.append('instantaneId', input.instantaneId);
  return f;
}

async function envoyer<T>(url: string, corps: FormData): Promise<T> {
  const res = await fetch(url, { method: 'POST', credentials: 'same-origin', body: corps });
  const json = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) {
    // Une COUPURE n'est pas un refus, et le dire change tout : la passerelle
    // rend du HTML, pas du JSON, donc `json.error` est vide et le message
    // tombait sur « 504 Gateway Time-out » — illisible, et faux puisque le
    // serveur travaillait encore. Constaté le 13/10/2026 sur l'initialisation.
    if (res.status === 502 || res.status === 503 || res.status === 504) {
      throw new Error(
        'La réponse a été coupée par le serveur au bout de 30 secondes. Le traitement a peut-être continué : rechargez l’écran pour voir où il en est.',
      );
    }
    throw new Error(json.error ?? `${res.status} ${res.statusText}`);
  }
  return json;
}

/**
 * L'état d'une tâche de fond : « en_cours » tant qu'elle travaille.
 *
 * `depuis` est l'instant du LANCEMENT (horloge du serveur, en millisecondes).
 * Le serveur l'envoyait déjà et ce type l'ignorait, si bien que l'écran ne
 * pouvait pas dire depuis combien de temps ça travaille — et sept minutes
 * normales ressemblaient alors à un blocage. C'est le serveur qui le porte et
 * non le navigateur, parce qu'un rechargement de la page ne doit PAS remettre le
 * compteur à zéro : c'est précisément le cas où la durée se lit avec inquiétude.
 */
export type EtatTache =
  | { statut: 'en_cours'; progression: string; depuis: number }
  | { statut: 'fini'; progression: string; resultat: unknown }
  | { statut: 'echec'; progression: string; erreur: string };

export const amorcageApi = {
  /** Lance l'analyse et rend l'identifiant de la tâche : elle dure des minutes. */
  analyser: (input: Parameters<typeof formulaire>[0]) =>
    envoyer<{ tacheId: string }>('/api/compta/amorcage/analyse', formulaire(input)),
  lancer: (input: Parameters<typeof formulaire>[0]) =>
    envoyer<{ tacheId: string }>('/api/compta/amorcage', formulaire(input)),
  /** Où en est la tâche. Une tâche inconnue rend une erreur qui le dit en clair. */
  suivre: (tacheId: string) => request<EtatTache>(`/api/compta/amorcage/tache/${tacheId}`),
};

/**
 * Cette erreur dit-elle que la tâche n'existe plus, ou juste qu'un appel a raté ?
 *
 * La distinction décide s'il faut ABANDONNER le suivi : une tâche inconnue est
 * une réponse (redémarrage, écran laissé ouvert), tandis qu'un hoquet de la
 * passerelle pendant les six minutes de travail — et il y a ~180 appels de
 * suivi — ne doit pas faire croire que tout est perdu alors que le serveur
 * travaille encore.
 */
export function tacheInconnue(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

